import styled from '@emotion/styled'
import { Box, Button, InfoBox, Modal, ModalProps, ProductInfoBox } from 'stylewhere/components'
import { T, __ } from 'stylewhere/i18n'
import React from 'react'
import {
  ConfirmModalParams,
  Items,
  ProductionOrder,
  ProductionOrderRow,
  ProductionOrderRows,
  Products,
  TmrItem,
  TmrProduct
} from 'stylewhere/api'
import { Skeleton } from '@chakra-ui/react'
import { getEncodingProductFields } from 'stylewhere/shared/utils'
import { EncodingExtensions } from 'stylewhere/extensions'

interface Props {
  title: string
  subtitle?: string
  isOpen?: boolean
  onConfirm?: () => void
  onClose: () => void
  style?: any
  labelClose?: string
  labelConfirm?: string
  /** if present show a product information box inside the confirmation modal */
  showDetailForProduct?: ConfirmModalParams['showDetailForProduct']
}

interface ProductDetails {
  product?: TmrProduct
  productionOrder?: ProductionOrder
}

export const ConfirmModal = (props: Props) => {
  const { isOpen, onClose, title, onConfirm, subtitle, labelClose, labelConfirm, showDetailForProduct: p } = props
  const [detailProduct, setDetailProduct] = React.useState<ProductDetails | undefined>(undefined)
  const [productLoading, setProductLoading] = React.useState<boolean>(false)

  const btn1 = { flex: 1, minWidth: 'auto', height: 60 }
  const btn2 = { flex: 1, minWidth: 'auto', marginLeft: 15, height: 60 }

  React.useEffect(() => {
    async function fetchProductDetail() {
      if (p && (p.productCode || p.identifierCode)) {
        try {
          setProductLoading(true)
          const productDetails: ProductDetails = {}
          if (p.productCode) {
            const products = await Products.searchCodes([p.productCode])
            if (products.content.length) {
              productDetails.product = products.content[0]
            }
          } else if (p.identifierCode) {
            const items = await Items.search<TmrItem>({ identifierCodes: [p.identifierCode] })
            if (items.content.length) {
              const product = items.content[0].product
              if (product) {
                productDetails.product = product
              }
            }
          }
          if (p.productionOrderRowId) {
            const productionOrderRow: ProductionOrderRow = await ProductionOrderRows.get(p.productionOrderRowId)
            if (productionOrderRow) {
              productDetails.productionOrder = productionOrderRow.productionOrder
            }
          }
          setDetailProduct(productDetails)
        } catch (error) {
          console.error(error)
        } finally {
          setProductLoading(false)
        }
      }
    }
    fetchProductDetail()
  }, [p])

  const size: ModalProps['size'] = p ? '4xl' : 'xl'

  return (
    <Modal visible={isOpen ?? true} onClose={onClose} size={size} margin="5" padding="1">
      <Box>
        <Title>{title}</Title>
        <Description>{subtitle}</Description>
      </Box>

      {p && (
        <Skeleton loading={productLoading}>
          <Box mb={5} flex row hcenter>
            <Box m={5} flex={4}>
              <ProductInfoBox
                product={detailProduct?.product}
                paddingCard={'20px 20px 20px 20px'}
                fields={getEncodingProductFields()}
              />
            </Box>
            <Box m={5} flex={2}>
              <InfoBox
                title={__(T.misc.work_order)}
                subtitle={detailProduct?.productionOrder?.code}
                data={detailProduct?.productionOrder}
                fields={EncodingExtensions.getProductionOrderFields()}
              />
            </Box>
          </Box>
        </Skeleton>
      )}

      <Box row mt={10}>
        <Button variant="default" title={labelClose || __(T.misc.cancel)} onClick={onClose} style={btn1} />
        <Button title={labelConfirm || __(T.misc.confirm)} onClick={onConfirm} style={btn2} />
      </Box>
    </Modal>
  )
}

const Title = styled.span`
  font-size: 26px;
  font-weight: 700;
  text-align: center;
  margin-top: 10px;
`

const Description = styled.label`
  font-weight: 500;
  padding: 25px 15px;
  font-size: 21px;
  white-space: pre-line;
  text-align: center;
  opacity: 0.5;
`
